import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type { ListPaginationInterface } from '@/interfaces/base/pagination/listPagination.interface';
import type { MenuItem } from '@/interfaces/components/menu/itemsMenu.interface';
import {
  clonePipelineDealsFilters,
  createDefaultPipelineDealsFilters,
} from '@/modules/pipeline/schemas/PipelineDealsFilters.schema';
import {
  getDefaultTableState,
  normalizeBoardEntry,
} from '@/modules/pipeline/stores/board/PipelineBoard.normalizers';
import {
  applyOptimisticDealMoveToEntry,
  rollbackOptimisticDealMoveEntry,
  withAppendedStagePage,
  withStagePageLoading,
  withTableLoading,
  withTablePage,
  withTablePageFailure,
} from '@/modules/pipeline/stores/board/PipelineBoard.reducers';
import {
  selectBoardColumns,
  selectIsTableLoading,
  selectStageIds,
  selectTablePagination,
  selectTableRows,
  selectTableState,
} from '@/modules/pipeline/stores/board/PipelineBoard.selectors';
import type {
  PipelineBoardCacheEntry,
  PipelineBoardCacheEntryInput,
  PipelineBoardMoveSnapshot,
  PipelineBoardTableRowView,
  PipelineDealCardViewRecord,
} from '@/modules/pipeline/types/Pipeline.types';
import type {
  GetPipelineRecord,
  PipelineSummaryRecord,
} from '@/modules/pipeline/types/PipelineAPI.types';
import type { PipelineDealsAppliedFilters } from '@/modules/pipeline/types/PipelineFilters.types';
import {
  buildPipelineIdsToUrlsMap,
  buildPipelineMenuOptions,
  buildPipelineUrlsToIdsMap,
  resolvePipelineIdByName,
  toPipelineRecord,
} from '@/modules/pipeline/utils/Pipeline.utils';
import { useUserStore } from '@/modules/user/stores/users.store';

export const usePipelineStore = defineStore('AtenaPipelineStore', () => {
  const userStore = useUserStore();
  const pipelines = ref<GetPipelineRecord[] | null>(null);
  const pipelineDetailsById = ref<Record<string, GetPipelineRecord>>({});
  const urls = ref<Record<string, string>>({});
  const currentPipelineId = ref<string | null>(null);
  const appliedFilters = ref<PipelineDealsAppliedFilters>(
    createDefaultPipelineDealsFilters(userStore.defaultFilterMode),
  );
  const filterKey = ref('');

  const pipelinesListFetchedAt = ref<number | null>(null);
  const isFetchingPipelinesList = ref(false);
  const pipelinesListError = ref<string | null>(null);

  const boardCacheByPipelineId = ref<Record<string, PipelineBoardCacheEntry>>(
    {},
  );
  const activeBoardPipelineId = ref<string | null>(null);
  const isFetchingDataForBoard = ref(false);
  const errorFetchingDataForBoard = ref<string | null>(null);

  const getBoardEntry = (
    pipelineId: string | null | undefined,
  ): PipelineBoardCacheEntry | null => {
    if (!pipelineId) {
      return null;
    }

    return boardCacheByPipelineId.value[pipelineId] ?? null;
  };

  const replaceBoardEntry = (
    pipelineId: string,
    entry: PipelineBoardCacheEntry,
  ): PipelineBoardCacheEntry => {
    boardCacheByPipelineId.value = {
      ...boardCacheByPipelineId.value,
      [pipelineId]: entry,
    };

    return entry;
  };

  const updateBoardEntry = (
    pipelineId: string,
    updater: (entry: PipelineBoardCacheEntry) => PipelineBoardCacheEntry,
  ): PipelineBoardCacheEntry | null => {
    const currentEntry = getBoardEntry(pipelineId);

    if (!currentEntry) {
      return null;
    }

    return replaceBoardEntry(pipelineId, updater(currentEntry));
  };

  const activeBoardState = computed(() => {
    return getBoardEntry(activeBoardPipelineId.value);
  });

  const activeBoardResponse = computed(() => {
    return activeBoardState.value?.response ?? null;
  });

  const pipelineUrlsById = computed(() => {
    return buildPipelineIdsToUrlsMap(urls.value);
  });

  const activeBoardColumns = computed(() => {
    return selectBoardColumns(activeBoardState.value);
  });

  const activeTableState = computed(() => {
    return selectTableState(activeBoardState.value);
  });

  const activeTableRows = computed(() => {
    return selectTableRows(activeBoardState.value);
  });

  const activeTablePagination = computed<ListPaginationInterface>(() => {
    return selectTablePagination(activeBoardState.value);
  });

  const activeStageIds = computed(() => {
    return selectStageIds(activeBoardState.value);
  });

  const isTableLoading = computed(() => {
    return selectIsTableLoading(activeBoardState.value);
  });

  const isBoardLoading = computed(() => isFetchingDataForBoard.value);
  const boardError = computed(() => errorFetchingDataForBoard.value);
  const listFetchedAt = computed(() => pipelinesListFetchedAt.value);

  const normalizePipelineListRecord = (
    pipeline: PipelineSummaryRecord | GetPipelineRecord,
  ): GetPipelineRecord => {
    const cachedDetail = pipelineDetailsById.value[pipeline.id];

    if (cachedDetail) {
      return {
        ...cachedDetail,
        name: pipeline.name,
        referenceId: pipeline.referenceId,
      };
    }

    return {
      id: pipeline.id,
      name: pipeline.name,
      referenceId: pipeline.referenceId,
      stages: 'stages' in pipeline ? pipeline.stages : [],
    };
  };

  const setAvailablePipelines = (
    payload: Array<PipelineSummaryRecord | GetPipelineRecord>,
    fetchedAt = Date.now(),
  ) => {
    pipelines.value = payload.map(normalizePipelineListRecord);
    urls.value = buildPipelineUrlsToIdsMap(payload);
    pipelinesListFetchedAt.value = fetchedAt;
    setPipelinesListError(null);
  };

  const setPipelineList = (
    payload: Array<PipelineSummaryRecord | GetPipelineRecord>,
    fetchedAt?: number,
  ) => {
    setAvailablePipelines(payload, fetchedAt);
  };

  const setPipelines = (
    payload: Array<PipelineSummaryRecord | GetPipelineRecord>,
  ) => {
    setAvailablePipelines(payload);
  };

  const setPipelineDetail = (pipeline: GetPipelineRecord) => {
    pipelineDetailsById.value = {
      ...pipelineDetailsById.value,
      [pipeline.id]: pipeline,
    };

    const currentPipelines = pipelines.value ?? [];
    const nextPipeline = normalizePipelineListRecord(pipeline);
    const pipelineExists = currentPipelines.some(
      (item) => item.id === pipeline.id,
    );

    pipelines.value = pipelineExists
      ? currentPipelines.map((item) =>
          item.id === pipeline.id ? nextPipeline : item,
        )
      : [...currentPipelines, nextPipeline];

    urls.value = buildPipelineUrlsToIdsMap(pipelines.value);
  };

  const setPipelinesListLoading = (isLoading: boolean) => {
    isFetchingPipelinesList.value = isLoading;
  };

  const setPipelinesListError = (error: string | null) => {
    pipelinesListError.value = error;
  };

  const setActiveBoardPipeline = (pipelineId: string | null) => {
    activeBoardPipelineId.value = pipelineId;
    currentPipelineId.value = pipelineId;
  };

  const beginBoardLoad = () => {
    isFetchingDataForBoard.value = true;
    errorFetchingDataForBoard.value = null;
  };

  const finishBoardLoad = () => {
    isFetchingDataForBoard.value = false;
    errorFetchingDataForBoard.value = null;
  };

  const failBoardLoad = (error: string | null) => {
    isFetchingDataForBoard.value = false;
    errorFetchingDataForBoard.value = error;
  };

  const clearBoardState = () => {
    activeBoardPipelineId.value = null;
    currentPipelineId.value = null;
    finishBoardLoad();
  };

  const clearPipelines = () => {
    pipelines.value = null;
    urls.value = {};
    currentPipelineId.value = null;
    appliedFilters.value = createDefaultPipelineDealsFilters(
      userStore.defaultFilterMode,
    );
    filterKey.value = '';
    pipelinesListFetchedAt.value = null;
    setPipelinesListLoading(false);
    setPipelinesListError(null);
    boardCacheByPipelineId.value = {};
    pipelineDetailsById.value = {};
    clearBoardState();
  };

  const pipelineMenuOptions = (): MenuItem[] => {
    if (!pipelines.value) return [];

    return buildPipelineMenuOptions(pipelines.value);
  };

  const getPipelineIdByName = (name: string): string | null => {
    return resolvePipelineIdByName(name, urls.value, pipelines.value ?? []);
  };

  const getPipelineSlugById = (id: string): string | null => {
    return pipelineUrlsById.value[id] ?? null;
  };

  const getPipelineRecordById = (
    pipelineId: string | null | undefined,
  ): GetPipelineRecord | null => {
    if (!pipelineId) {
      return null;
    }

    const cachedBoardResponse = getBoardEntry(pipelineId)?.response;

    if (cachedBoardResponse) {
      return toPipelineRecord(cachedBoardResponse);
    }

    const cachedDetail = pipelineDetailsById.value[pipelineId];

    if (cachedDetail) {
      return toPipelineRecord(cachedDetail);
    }

    const pipeline = pipelines.value?.find((item) => item.id === pipelineId);

    return pipeline ? toPipelineRecord(pipeline) : null;
  };

  const getPipelineDetailById = (
    pipelineId: string | null | undefined,
  ): GetPipelineRecord | null => {
    if (!pipelineId) {
      return null;
    }

    return pipelineDetailsById.value[pipelineId] ?? null;
  };

  const activePipelineRecord = computed(() => {
    return getPipelineRecordById(currentPipelineId.value);
  });

  const setCurrentPipelineId = (nameOrSlug: string) => {
    currentPipelineId.value = getPipelineIdByName(nameOrSlug);
  };

  const setCurrentPipelineIdBySlug = (slug: string): string | null => {
    const pipelineId = getPipelineIdByName(slug);
    currentPipelineId.value = pipelineId;
    return pipelineId;
  };

  const setCurrentPipelineIdById = (id: string | null) => {
    currentPipelineId.value = id;
  };

  const syncAppliedFilters = (payload: {
    filters: PipelineDealsAppliedFilters;
    filterKey: string;
  }) => {
    appliedFilters.value = clonePipelineDealsFilters(payload.filters);
    filterKey.value = payload.filterKey;
  };

  const getAppliedFiltersSnapshot = (
    pipelineId?: string | null,
  ): PipelineDealsAppliedFilters => {
    const resolvedPipelineId =
      pipelineId ?? activeBoardPipelineId.value ?? currentPipelineId.value;
    const entry = getBoardEntry(resolvedPipelineId);

    return clonePipelineDealsFilters(entry?.filters ?? appliedFilters.value);
  };

  const cacheBoard = (
    pipelineId: string,
    entryInput: PipelineBoardCacheEntryInput,
  ) => {
    const entry = normalizeBoardEntry(entryInput);

    replaceBoardEntry(pipelineId, entry);
    setActiveBoardPipeline(pipelineId);
    finishBoardLoad();
  };

  const beginStagePageLoad = (payload: {
    pipelineId: string;
    stageId: string;
  }) => {
    updateBoardEntry(payload.pipelineId, (entry) =>
      withStagePageLoading(entry, {
        stageId: payload.stageId,
        isLoading: true,
      }),
    );
  };

  const appendStagePage = (payload: {
    pipelineId: string;
    stageId: string;
    deals: Array<PipelineDealCardViewRecord>;
    page: number;
    hasMore?: boolean;
  }) => {
    updateBoardEntry(payload.pipelineId, (entry) =>
      withAppendedStagePage(entry, {
        stageId: payload.stageId,
        deals: payload.deals,
        page: payload.page,
        hasMore: payload.hasMore,
      }),
    );
  };

  const failStagePageLoad = (payload: {
    pipelineId: string;
    stageId: string;
  }) => {
    updateBoardEntry(payload.pipelineId, (entry) =>
      withStagePageLoading(entry, {
        stageId: payload.stageId,
        isLoading: false,
      }),
    );
  };

  const beginTablePageLoad = (payload: { pipelineId: string }) => {
    updateBoardEntry(payload.pipelineId, (entry) =>
      withTableLoading(entry, true),
    );
  };

  const setTablePage = (payload: {
    pipelineId: string;
    rows: Array<PipelineBoardTableRowView>;
    pagination: ListPaginationInterface;
    isLoading?: boolean;
  }) => {
    updateBoardEntry(payload.pipelineId, (entry) =>
      withTablePage(entry, {
        rows: payload.rows,
        pagination: payload.pagination,
        isLoading: payload.isLoading,
      }),
    );
  };

  const failTablePageLoad = (payload: {
    pipelineId: string;
    clearIfEmpty?: boolean;
  }) => {
    updateBoardEntry(payload.pipelineId, (entry) =>
      withTablePageFailure(entry, payload.clearIfEmpty ?? false),
    );
  };

  const applyOptimisticDealMove = (payload: {
    pipelineId: string;
    dealId: string;
    sourceStageId: string;
    destinationStageId: string;
  }): PipelineBoardMoveSnapshot => {
    const currentEntry = getBoardEntry(payload.pipelineId);

    if (!currentEntry) {
      return {
        pipelineId: payload.pipelineId,
        columns: [],
        tableState: getDefaultTableState(),
      };
    }

    const { nextEntry, snapshot } = applyOptimisticDealMoveToEntry(
      currentEntry,
      payload,
    );

    replaceBoardEntry(payload.pipelineId, nextEntry);

    return snapshot;
  };

  const rollbackOptimisticDealMove = (snapshot: PipelineBoardMoveSnapshot) => {
    updateBoardEntry(snapshot.pipelineId, (entry) =>
      rollbackOptimisticDealMoveEntry(entry, snapshot),
    );
  };

  return {
    activeBoardColumns,
    activeBoardPipelineId,
    activeBoardResponse,
    activeBoardState,
    activeStageIds,
    activeTablePagination,
    activeTableRows,
    activeTableState,
    appliedFilters,
    boardError,
    filterKey,
    getBoardEntry,
    getPipelineDetailById,
    getPipelineIdByName,
    getPipelineRecordById,
    getPipelineSlugById,
    isBoardLoading,
    isFetchingPipelinesList,
    isTableLoading,
    pipelineDetailsById,
    pipelineMenuOptions,
    pipelineUrlsById,
    pipelines,
    pipelinesListError,
    pipelinesListFetchedAt,
    listFetchedAt,
    activePipelineRecord,
    appendStagePage,
    applyOptimisticDealMove,
    beginBoardLoad,
    beginStagePageLoad,
    beginTablePageLoad,
    cacheBoard,
    clearBoardState,
    clearPipelines,
    currentPipelineId,
    failBoardLoad,
    failStagePageLoad,
    failTablePageLoad,
    finishBoardLoad,
    getAppliedFiltersSnapshot,
    rollbackOptimisticDealMove,
    setActiveBoardPipeline,
    setAvailablePipelines,
    setPipelineDetail,
    setPipelineList,
    setCurrentPipelineId,
    setCurrentPipelineIdById,
    setCurrentPipelineIdBySlug,
    setPipelines,
    setPipelinesListError,
    setPipelinesListLoading,
    syncAppliedFilters,
    setTablePage,
    urls,
  };
});
